import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { API_BASE, getImageUrl } from '../../utils/api'

import Navbar from '../../components/navbar/navbar'
import BlogSidebar from '../../components/blog-sidebar'
import Footer from '../../components/footer'
import ScrollToTop from '../../components/scroll-to-top';
import { toast } from 'react-toastify';

interface Comment {
    name: string;
    email?: string;
    text: string;
    date: string;
}

interface Blog {
    _id: string;
    title: string;
    date: string;
    desc: string;
    image: string;
    views?: number;
    likes?: number;
    category?: string;
    authorName?: string;
    tags?: string[];
    comments?: Comment[];
}

export default function BlogDetail() {
    let params = useParams()
    let id: any = params.id
    const [blog, setBlog] = useState<Blog | null>(null)
    const [loading, setLoading] = useState(true)

    // Comment form state
    const [commentData, setCommentData] = useState({ name: '', email: '', text: '' })
    const [submitting, setSubmitting] = useState(false)

    const fetchBlog = React.useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/blogs/${id}`)
            const data = await res.json()
            setBlog(data)
        } catch (err) {
            console.error(err)
        } finally {
            setLoading(false)
        }
    }, [id])

    useEffect(() => {
        fetchBlog()
        // Track view - only once when id changes
        fetch(`${API_BASE}/blogs/${id}/view`, { method: 'PATCH' })
    }, [id, fetchBlog])

    const handleCommentSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!commentData.name || !commentData.text) {
            toast.error('Please provide name and comment content')
            return
        }
        setSubmitting(true)
        try {
            const res = await fetch(`${API_BASE}/blogs/${id}/comment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(commentData)
            })
            if (res.ok) {
                toast.success('Your review shared successfully!')
                setCommentData({ name: '', email: '', text: '' })
                fetchBlog() // Refresh comments
            } else {
                toast.error('Failed to post comment')
            }
        } catch (err) {
            toast.error('Server error')
        } finally {
            setSubmitting(false)
        }
    }

    const handleLike = async () => {
        if (!blog) return
        try {
            const res = await fetch(`${API_BASE}/blogs/${id}/like`, { method: 'PATCH' })
            const data = await res.json()
            setBlog(data)
            toast.success("Article liked!");
        } catch (err) {
            console.error(err)
        }
    }

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen bg-[#f8f9fa]">
            <div className="w-12 h-12 border-4 border-slate-100 border-t-[#001b4a] rounded-full animate-spin"></div>
        </div>
    )

    return (
        <div className="bg-[#f8f9fa]">
            <Navbar transparent={false} />

            {/* Hero Section */}
            <section className="pt-32 pb-24 bg-[#001b4a] relative overflow-hidden text-center">
                <div className="absolute top-[-50px] right-[-50px] w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-5 left-5 w-32 h-32 bg-[#f1ae24]/10 rounded-full blur-2xl"></div>
                
                <div className="container-fluid mx-auto px-4 md:px-10 lg:px-16 relative z-10">
                    <div className="max-w-4xl mx-auto">
                        <nav className="flex justify-center mb-8">
                            <ol className="flex items-center gap-2 px-6 py-2 bg-white/10 backdrop-blur-md rounded-sm text-sm font-semibold">
                                <li className="flex items-center gap-2">
                                    <Link to="/" className="text-white/60 hover:text-white transition-colors">Home</Link>
                                    <i className="fa-solid fa-chevron-right text-[8px] text-white/30"></i>
                                </li>
                                <li className="flex items-center gap-2">
                                    <Link to="/blog" className="text-white/60 hover:text-white transition-colors">Blog</Link>
                                    <i className="fa-solid fa-chevron-right text-[8px] text-white/30"></i>
                                </li>
                                <li className="text-[#f1ae24]">Detail</li>
                            </ol>
                        </nav>
                        
                        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight tracking-tight">
                            {blog?.title || 'Modern Real Estate Trends & Insights'}
                        </h1>
                        
                        <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
                             <div className="flex items-center gap-3 bg-white/5 px-4 py-2 rounded-sm border border-white/5">
                                <div className="w-8 h-8 rounded-sm bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                                    {blog?.authorName?.charAt(0) || 'A'}
                                </div>
                                <span className="text-white text-sm font-semibold">{blog?.authorName || 'Admin'}</span>
                            </div>
                            <div className="flex items-center gap-3 text-white/60 text-[10px] font-bold uppercase tracking-widest">
                                <i className="fa-regular fa-calendar-days text-[#f1ae24]"></i>
                                <span>{blog?.date || 'March 11, 2025'}</span>
                            </div>
                            <div className="flex items-center gap-3 text-white/60 text-xs font-semibold">
                                <i className="fa-regular fa-eye text-[#f1ae24]"></i>
                                <span>{blog?.views || 0} Views</span>
                            </div>
                            <div className="flex items-center gap-3 text-white/60 text-xs font-semibold cursor-pointer hover:text-white transition-colors" onClick={handleLike}>
                                <i className="fa-regular fa-heart text-[#f1ae24]"></i>
                                <span>{blog?.likes || 0} Likes</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Content Section */}
            <section className="relative mt-[-80px] pb-24">
                <div className="container-fluid mx-auto px-4 md:px-10 lg:px-16">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        {/* Main Content */}
                        <div className="lg:col-span-8">
                            <div className="bg-white rounded-sm shadow-2xl overflow-hidden border border-slate-100 mb-8">
                                {/* Featured Image */}
                                <div className="relative w-full overflow-hidden bg-slate-100 flex items-center justify-center">
                                    <img 
                                        className="w-full h-auto max-h-[600px] object-contain" 
                                        src={blog?.image ? getImageUrl(blog?.image) : 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80'} 
                                        alt={blog?.title} 
                                    />
                                    {blog?.category && (
                                        <div className="absolute top-8 left-8 bg-[#f1ae24] text-black px-5 py-2 rounded-sm font-bold text-xs shadow-xl border border-white/20">
                                            {blog.category}
                                        </div>
                                    )}
                                </div>

                                 <div className="p-5 md:p-10 mb-5">
                                    <div className="blog-content mb-12 prose max-w-none text-black font-medium leading-[2] text-justify">
                                        {blog ? (
                                            <div dangerouslySetInnerHTML={{ __html: blog.desc }}></div>
                                        ) : (
                                            <p className="text-lg text-slate-400 font-normal">No content available for this article.</p>
                                        )}
                                    </div>


                                    {/* Share & Tags */}
                                    <div className="pt-12 border-t border-slate-100 flex flex-wrap items-center justify-between gap-8">
                                        <div className="flex flex-wrap items-center gap-4">
                                            <span className="text-xs font-bold text-[#001b4a]">Tags:</span>
                                            <div className="flex flex-wrap gap-2">
                                                {blog?.tags?.map((tag: string, i: number) => (
                                                     <span key={i} className="px-5 py-2 bg-slate-50 text-black rounded-sm text-xs font-semibold hover:bg-[#001b4a] hover:text-white transition-all cursor-default">
                                                        #{tag}
                                                    </span>
                                                )) || <span className="text-slate-300 text-xs font-semibold italic">No tags</span>}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <span className="text-xs font-bold text-[#001b4a]">Share:</span>
                                            <div className="flex items-center gap-2">
                                                {[
                                                    { i: 'fa-facebook-f', c: 'bg-[#1877F2]' },
                                                    { i: 'fa-twitter', c: 'bg-[#1DA1F2]' },
                                                    { i: 'fa-linkedin-in', c: 'bg-[#0A66C2]' }
                                                ].map((s, i) => (
                                                     <button key={i} className={`w-9 h-9 flex items-center justify-center rounded-sm text-white text-xs ${s.c} hover:shadow-lg transition-all active:scale-95`}>
                                                        <i className={`fab ${s.i}`}></i>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Author Card */}
                            <div className="bg-white rounded-sm shadow-sm p-8 border border-slate-100 transition-all hover:shadow-xl group">
                                <div className="flex flex-col md:flex-row items-center gap-8">
                                     <div className="w-24 h-24 rounded-sm overflow-hidden shrink-0 shadow-lg group-hover:rotate-3 transition-transform duration-500">
                                        <div className="w-full h-full bg-gradient-to-br from-[#001b4a] to-blue-600 flex items-center justify-center text-white text-3xl font-bold">
                                            {blog?.authorName?.charAt(0) || 'A'}
                                        </div>
                                    </div>
                                    <div className="text-center md:text-left">
                                        <div className="flex flex-wrap items-center justify-center md:justify-between gap-4 mb-3">
                                            <h5 className="text-xl font-bold text-[#001b4a]">{blog?.authorName || 'Rosalina William'}</h5>
                                            <div className="flex items-center gap-3">
                                                {['fa-facebook-f', 'fa-twitter', 'fa-linkedin-in', 'fa-instagram'].map((ico, i) => (
                                                    <button key={i} className="text-slate-300 hover:text-[#f1ae24] transition-colors"><i className={`fab ${ico}`}></i></button>
                                                ))}
                                            </div>
                                        </div>
                                        <p className="text-black text-sm font-medium leading-relaxed">
                                            Premium real estate insights by {blog?.authorName || 'our lead analyst'}. Dedicated to bringing you the most accurate and profitable market insights in the industry.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Comments Section */}
                             <div className="bg-white rounded-sm shadow-sm p-8 md:p-12 border border-slate-100 mb-8 overflow-hidden group">
                                <h3 className="text-2xl font-bold text-[#001b4a] mb-10 flex items-center gap-4">
                                    <span className="w-2 h-8 bg-[#f1ae24] rounded-full group-hover:h-12 transition-all"></span>
                                    Article <span className="text-[#f1ae24]">Discussions</span>
                                </h3>
                                
                                <div className="space-y-12 mb-16">
                                    {blog?.comments && blog.comments.length > 0 ? (
                                        blog.comments.map((comment, i) => (
                                            <div key={i} className={`flex gap-6 animate-in fade-in slide-in-from-left-4 duration-500`}>
                                             <div className="w-14 h-14 rounded-sm bg-slate-50 flex items-center justify-center text-[#001b4a] font-bold shrink-0 border border-slate-100 shadow-sm">
                                                    {comment.name.charAt(0)}
                                                </div>
                                                <div className="flex-grow">
                                                    <div className="flex flex-wrap items-center justify-between mb-2">
                                                        <h6 className="text-sm font-bold text-[#001b4a]">{comment.name}</h6>
                                                        <span className="text-xs text-slate-400 font-semibold">{comment.date}</span>
                                                    </div>
                                                    <p className="text-black text-sm font-medium leading-relaxed mb-4">{comment.text}</p>
                                                    <button className="text-[#f1ae24] hover:text-[#001b4a] text-xs font-bold flex items-center gap-2 transition-colors">
                                                        <i className="fa-solid fa-reply"></i> Reply
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                         <div className="text-center py-10 bg-slate-50 rounded-sm border border-dashed border-slate-200">
                                            <i className="fa-regular fa-comments text-3xl text-slate-200 mb-4 block"></i>
                                            <p className="text-sm font-semibold text-slate-400">No reviews shared yet. Be the first!</p>
                                        </div>
                                    )}
                                </div>

                                <div className="pt-12 border-t border-slate-100">
                                    <h5 className="text-lg font-bold text-[#001b4a] mb-8">Contribute to discussion</h5>
                                    <form className="space-y-4" onSubmit={handleCommentSubmit}>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="relative group/field">
                                                <input 
                                                    type="text" 
                                                    className="w-full px-6 py-2.5 bg-slate-50 border-none rounded-sm text-base font-semibold text-black placeholder:text-slate-300 focus:ring-2 focus:ring-[#f1ae24]/20 transition-all shadow-inner" 
                                                    placeholder="Your name"
                                                    value={commentData.name}
                                                    onChange={e => setCommentData({ ...commentData, name: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div className="relative group/field">
                                                <input 
                                                    type="email" 
                                                    className="w-full px-6 py-2.5 bg-slate-50 border-none rounded-sm text-base font-semibold text-black placeholder:text-slate-300 focus:ring-2 focus:ring-[#f1ae24]/20 transition-all shadow-inner" 
                                                    placeholder="Email"
                                                    value={commentData.email}
                                                    onChange={e => setCommentData({ ...commentData, email: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                        <div className="relative group/field">
                                            <textarea 
                                                className="w-full px-6 py-3 bg-slate-50 border-none rounded-sm text-base font-semibold text-black placeholder:text-slate-300 focus:ring-2 focus:ring-[#f1ae24]/20 transition-all shadow-inner min-h-[120px]" 
                                                placeholder="Write your review here..."
                                                value={commentData.text}
                                                onChange={e => setCommentData({ ...commentData, text: e.target.value })}
                                                required
                                            ></textarea>
                                        </div>
                                        <button 
                                            type="submit"
                                            disabled={submitting}
                                             className="px-12 py-4 bg-[#001b4a] hover:bg-black text-white rounded-sm font-bold text-base shadow-xl transition-all active:scale-95 group/btn flex items-center gap-3 disabled:opacity-50"
                                        >
                                            {submitting ? 'Submitting...' : 'Submit Review'}
                                            <i className="fa-solid fa-paper-plane text-xs group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"></i>
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>

                        {/* Sidebar */}
                        <div className="lg:col-span-4">
                            <BlogSidebar />
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
            <ScrollToTop />
        </div>
    )
}
